/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export const APP_NAME = "The Foundry";
export const APP_DESCRIPTION = "The dedicated launchpad application for HOT Network - The premier Content Creator Platform & AI Infrastructure.";

// Fees & Economics
export const SOL_MINT_FEE = 0.1; // Base SOL Fee (SPL Token)
export const SOL_NFT_MINT_FEE = 0.25; // Base SOL Fee (NFT)
export const SOL_OPTION_FEE = 0.1; // Cost per advanced option
export const SUI_MINT_FEE = 50; // SUI Fee (Package Publish + Treasury Fee)
export const SUI_OPTION_FEE = 10; // Cost per advanced option

// Solana Network Costs (Approximate)
export const SOL_RENT_PER_BYTE = 0.00000696; // ~6.96 Lamports per byte year
export const SOL_ACCOUNT_BASE_RENT = 0.00089; // Empty account rent
export const SOL_MINT_ACCOUNT_SIZE = 82;
export const SOL_METADATA_ACCOUNT_BASE_SIZE = 679; // Max size usually
export const SOL_TX_FEE = 0.000005; // Per signature

// Priority Fees
export const COMPUTE_UNIT_PRICE_MICRO_LAMPORTS = 1000; 
export const COMPUTE_UNIT_LIMIT = 300_000;

// Liquidity & Market Fees
export const OPENBOOK_MARKET_FEE_SOL = 2.8; // ~3 SOL usually for Legacy AMM
export const LP_LOCK_FEE_SOL = 0.5;
export const LP_BURN_FEE_SOL = 0.1;

export const PLATFORM_TRADE_FEE_BPS = 25; // 0.25%
export const RENT_EXEMPTION_SOL = 0.002039; // Exact rent for Token Account

// Raydium Constants
export const RAYDIUM_CPMM_PROGRAM_ID = "CPMMoo8L3F4NbTegBCKVNunggL7H1ZpdTHKxQB5qKP1C"; // Standard AMM (Token-2022 supported)
export const RAYDIUM_AMM_V4_PROGRAM_ID = "675kPX9MHTjS2zt1qfr1NYHuzeLXfQM9H24wFSUt1Mp8"; // Legacy AMM

// Bonding Curve Configuration
export const BONDING_CURVE_TARGET_SOL = 85; 
export const BONDING_CURVE_TARGET_SUI = 5000; 
export const VIRTUAL_SOL_RESERVES = 30; 
export const VIRTUAL_SUI_RESERVES = 1500; 
export const INITIAL_TOKEN_SUPPLY = 1_000_000_000; 

// AI Configuration
export const AI_CONFIDENCE_THRESHOLD = 75; // Minimum score to show "High Confidence"
export const AI_REFRESH_INTERVAL = 30000; // 30s

// Market Configuration
export const MARKET_REFRESH_INTERVAL = 30000; // 30s

// Addresses
export const TREASURY_WALLET_SOL = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_TREASURY_WALLET) ? import.meta.env.VITE_TREASURY_WALLET : "HotTreasuryDaoSolAddress7xKX9jL2"; 
export const TREASURY_WALLET_SUI = "0x4d558c94baf63e0d24dbddccecd62c949946673eee8c1781c105cb44dd45b4b9"; // Updated SUI Treasury

export const TREASURY_WALLET = TREASURY_WALLET_SOL; // Default export for backwards compat

// Helper to safely access env
const getEnv = (key: string) => {
  try {
    // @ts-ignore
    return (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env[key] : undefined;
  } catch (e) {
    return undefined;
  }
};

const HELIUS_KEY = getEnv('VITE_HELIUS_API_KEY');
const TATUM_KEY = getEnv('VITE_TATUM_API_KEY');

// Robust RPC Configuration
export const RPC_ENDPOINTS = {
    SOL: {
        MAINNET: [
            HELIUS_KEY ? `https://mainnet.helius-rpc.com/?api-key=${HELIUS_KEY}` : null, // Premium Helius
            TATUM_KEY ? `https://solana-mainnet.gateway.tatum.io/` : null, // Premium Tatum
            "https://mainnet.helius-rpc.com/?api-key=demo", // Public Helius
            "https://api.mainnet-beta.solana.com", // Foundation
            "https://rpc.ankr.com/solana", // Ankr Public
            "https://solana-mainnet.g.alchemy.com/v2/demo" // Alchemy Demo
        ].filter(Boolean) as string[],
        DEVNET: [
            HELIUS_KEY ? `https://devnet.helius-rpc.com/?api-key=${HELIUS_KEY}` : null,
            TATUM_KEY ? `https://solana-devnet.gateway.tatum.io/` : null, // Premium Tatum
            "https://devnet.helius-rpc.com/?api-key=demo",
            "https://api.devnet.solana.com",
            "https://rpc.ankr.com/solana_devnet"
        ].filter(Boolean) as string[]
    },
    SUI: {
        MAINNET: [
            "https://fullnode.mainnet.sui.io:443",
            "https://sui-mainnet.nodeinfra.com"
        ],
        DEVNET: [
            "https://fullnode.devnet.sui.io:443"
        ]
    }
};

export const WS_ENDPOINT = "wss://api.hotnetwork.app/v1/ws";

// Export default primaries for initial state
export const SOLANA_MAINNET_RPC = RPC_ENDPOINTS.SOL.MAINNET[0];
export const SOLANA_DEVNET_RPC = RPC_ENDPOINTS.SOL.DEVNET[0];

export const SUI_MAINNET_RPC = RPC_ENDPOINTS.SUI.MAINNET[0];
export const SUI_DEVNET_RPC = RPC_ENDPOINTS.SUI.DEVNET[0];

// External Services
export const PINATA_GATEWAY = "https://gateway.pinata.cloud/ipfs/";
export const PINATA_GROUP_URL = "https://app.pinata.cloud/ipfs/groups/cb0fd83e-e399-44a7-bc92-882d8b71cf7a";
export const HOT_TOKEN_CID = "bafybeicjrjwd7ckv4pdmupbeuqeaevvr3qhbhedsciiphaeh6zagdx6rxu";

export const JUPITER_TOKEN_LIST_URL = "https://token.jup.ag/strict";